import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import { useMerchantId } from '@/hooks/useMerchantId';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Search, ChevronsUpDown, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';
import TicketDetail from './TicketDetail';
import { BranchFilter } from '@/components/BranchFilter';
import { apiErrorMessage } from '@/lib/api-error';

type Ticket = components['schemas']['TicketResource'];

const priorityLabel = (p: string) => i18n.t(`tick_common.priority.${p}`);
const priorityColors: Record<string, string> = {
  low: 'bg-muted text-muted-foreground',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  high: 'bg-destructive/10 text-destructive',
};
const statusLabel = (s: string) => i18n.t(`tick_common.status.${s}`);
const statusColors: Record<string, string> = {
  new: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  reviewing: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  closed: 'bg-muted text-muted-foreground',
};

const BUILTIN_MODULE_CODES = ['hisob_app', 'customers', 'murabaha', 'inventory', 'products', 'sales', 'pricing', 'branches', 'reports', 'pos'];
const builtinModules = (): Record<string, string> =>
  Object.fromEntries(BUILTIN_MODULE_CODES.map((c) => [c, i18n.t(`tick_common.modules.${c}`)]));

export default function Tickets() {
  const { t } = useTranslation();
  const BUILTIN_MODULES = builtinModules();
  const { merchantId } = useMerchantId();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [priority, setPriority] = useState<string>('medium');
  const [linkedModule, setLinkedModule] = useState('');
  const [linkedProductId, setLinkedProductId] = useState<number | null>(null);
  const [linkedCustomerId, setLinkedCustomerId] = useState<number | null>(null);
  const [customerComboOpen, setCustomerComboOpen] = useState(false);

  const { data: ticketsData, isLoading } = useQuery({ queryKey: ['/tickets'], enabled: !!merchantId });
  const tickets = (ticketsData?.data ?? []) as Ticket[];

  const { data: productsData } = useQuery({ queryKey: ['/products'], enabled: !!merchantId });
  const products = productsData?.data ?? [];

  const { data: customersData } = useQuery({ queryKey: ['/customers'], enabled: !!merchantId });
  const customers = customersData?.data ?? [];

  const { data: modulesData } = useQuery({ queryKey: ['/modules'] });
  const modules = modulesData?.data ?? [];

  const createMutation = useMutation({
    mutationFn: async () => {
      const { error } = await apiClient.POST('/tickets', {
        body: {
          subject,
          body: body || undefined,
          priority: priority as 'low' | 'medium' | 'high',
          product_id: linkedProductId ?? undefined,
          module_code: linkedModule || undefined,
          customer_id: linkedCustomerId ?? undefined,
        },
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/tickets'] });
      toast.success(t('tick_list.created'));
      setCreateOpen(false);
      resetForm();
    },
    onError: (e) => toast.error(apiErrorMessage(e, t('tick_list.error'))),
  });

  const resetForm = () => {
    setSubject('');
    setBody('');
    setPriority('medium');
    setLinkedModule('');
    setLinkedProductId(null);
    setLinkedCustomerId(null);
  };

  const filtered = tickets.filter((t) => {
    const matchSearch = !search || t.subject.toLowerCase().includes(search.toLowerCase()) || t.ticket_number.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || t.status === statusFilter;
    const matchPriority = priorityFilter === 'all' || t.priority === priorityFilter;
    const matchBranch = branchFilter === 'all' || String(t.branch_id) === branchFilter;
    return matchSearch && matchStatus && matchPriority && matchBranch;
  });

  if (selectedTicketId) {
    return <TicketDetail ticketId={selectedTicketId} onBack={() => setSelectedTicketId(null)} />;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('tick_list.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('tick_list.subtitle')}</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button><Plus size={16} className="mr-2" />{t('tick_list.new')}</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('tick_list.createTitle')}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>{t('tick_list.subject')}</Label>
                <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder={t('tick_list.subjectPh')} />
              </div>
              <div>
                <Label>{t('tick_list.body')}</Label>
                <Textarea value={body} onChange={e => setBody(e.target.value)} placeholder={t('tick_list.bodyPh')} rows={4} />
              </div>
              <div>
                <Label>{t('tick_list.priority')}</Label>
                <Select value={priority} onValueChange={setPriority}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">{t('tick_common.priority.low')}</SelectItem>
                    <SelectItem value="medium">{t('tick_common.priority.medium')}</SelectItem>
                    <SelectItem value="high">{t('tick_common.priority.high')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('tick_list.linkProduct')}</Label>
                <Select value={linkedProductId ? String(linkedProductId) : '__none__'} onValueChange={v => setLinkedProductId(v === '__none__' ? null : Number(v))}>
                  <SelectTrigger><SelectValue placeholder={t('tick_list.choose')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t('tick_list.none')}</SelectItem>
                    {products.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.name} ({p.sku})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('tick_list.linkModule')}</Label>
                <Select value={linkedModule || '__none__'} onValueChange={v => setLinkedModule(v === '__none__' ? '' : v)}>
                  <SelectTrigger><SelectValue placeholder={t('tick_list.choose')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t('tick_list.none')}</SelectItem>
                    {Object.entries(BUILTIN_MODULES).map(([code, label]) => (
                      <SelectItem key={code} value={code}>{label}</SelectItem>
                    ))}
                    {modules.filter((m) => !Object.keys(BUILTIN_MODULES).includes(m.code ?? '')).map((m) => (
                      <SelectItem key={m.code} value={m.code ?? ''}>🔌 {m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('tick_list.linkCustomer')}</Label>
                <Popover open={customerComboOpen} onOpenChange={setCustomerComboOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" aria-expanded={customerComboOpen} className="w-full justify-between font-normal">
                      {linkedCustomerId
                        ? customers.find((c) => c.id === linkedCustomerId)?.full_name || t('tick_list.selected')
                        : t('tick_list.customerPh')}
                      <div className="flex items-center gap-1 ml-2">
                        {linkedCustomerId && (
                          <X
                            size={14}
                            className="text-muted-foreground hover:text-foreground"
                            onClick={(e) => { e.stopPropagation(); setLinkedCustomerId(null); }}
                          />
                        )}
                        <ChevronsUpDown size={14} className="text-muted-foreground" />
                      </div>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                    <Command>
                      <CommandInput placeholder={t('tick_list.customerSearchPh')} />
                      <CommandList>
                        <CommandEmpty>{t('tick_list.customerNotFound')}</CommandEmpty>
                        <CommandGroup>
                          {customers.map((c) => (
                            <CommandItem
                              key={c.id}
                              value={`${c.full_name} ${c.phone}`}
                              onSelect={() => {
                                setLinkedCustomerId(c.id);
                                setCustomerComboOpen(false);
                              }}
                            >
                              <Check size={14} className={cn("mr-2", linkedCustomerId === c.id ? "opacity-100" : "opacity-0")} />
                              <div>
                                <p className="text-sm">{c.full_name}</p>
                                <p className="text-xs text-muted-foreground">{c.phone}</p>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <Button
                className="w-full"
                disabled={!subject.trim() || createMutation.isPending}
                onClick={() => createMutation.mutate()}
              >
                {createMutation.isPending ? t('tick_list.creating') : t('tick_list.create')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder={t('tick_list.search')} value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <BranchFilter value={branchFilter} onChange={setBranchFilter} />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder={t('tick_list.statusPh')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('tick_list.allStatuses')}</SelectItem>
            <SelectItem value="new">{t('tick_common.status.new')}</SelectItem>
            <SelectItem value="reviewing">{t('tick_common.status.reviewing')}</SelectItem>
            <SelectItem value="resolved">{t('tick_common.status.resolved')}</SelectItem>
            <SelectItem value="closed">{t('tick_common.status.closed')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-[160px]"><SelectValue placeholder={t('tick_list.priorityPh')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('tick_list.all')}</SelectItem>
            <SelectItem value="high">{t('tick_common.priority.high')}</SelectItem>
            <SelectItem value="medium">{t('tick_common.priority.medium')}</SelectItem>
            <SelectItem value="low">{t('tick_common.priority.low')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">{t('tick_list.loading')}</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">{t('tick_list.notFound')}</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('tick_list.colNumber')}</TableHead>
                  <TableHead>{t('tick_list.colSubject')}</TableHead>
                  <TableHead>{t('tick_list.colPriority')}</TableHead>
                  <TableHead>{t('tick_list.colStatus')}</TableHead>
                  <TableHead>{t('tick_list.colCreator')}</TableHead>
                  <TableHead>{t('tick_list.colBranch')}</TableHead>
                  <TableHead>{t('tick_list.colLinked')}</TableHead>
                  <TableHead>{t('tick_list.colDate')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    className="cursor-pointer"
                    onClick={() => setSelectedTicketId(ticket.id)}
                  >
                    <TableCell className="font-mono text-xs">{ticket.ticket_number}</TableCell>
                    <TableCell className="font-medium max-w-[250px] truncate">{ticket.subject}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={priorityColors[ticket.priority]}>
                        {priorityLabel(ticket.priority)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={statusColors[ticket.status]}>
                        {statusLabel(ticket.status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{ticket.created_by_user?.name || '—'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{ticket.branch?.name || '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {ticket.product?.name && <span className="block">📦 {ticket.product.name}</span>}
                      {ticket.module_code && <span className="block">🔌 {BUILTIN_MODULES[ticket.module_code] || modules.find((m) => m.code === ticket.module_code)?.name || ticket.module_code}</span>}
                      {ticket.customer?.full_name && <span className="block">👤 {ticket.customer.full_name}</span>}
                      {!ticket.product && !ticket.module_code && !ticket.customer && '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {format(new Date(ticket.created_at), 'dd.MM.yyyy HH:mm')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
